import React, { useRef } from 'react';
import map from 'lodash/fp/map';
import flow from 'lodash/fp/flow';
import Div from '../../components/Element/Div/Div';
import Gutter from '../../components/Gutter/Gutter';
import Button from '../../components/Button/Button';
import styles from './FileUploader.module.scss';
import wrapEachComponentWithKey from '../../shared/utils/wrapEachComponentWithKey/wrapEachComponentWithKey';
import File from '../File/File';

const normalizeType = (type: any) => `.${type.toLowerCase()}`;
const typesToAttribute = (types: any) => types.map(normalizeType).join(',');

export type FileItemShape = {
  /** Name of the file like example.pdf */
  name: string;
  /** URL for downloading the file */
  url?: string;
  /** An action which initiates deleting the file */
  onDelete?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  /** State for when we are processing an api call */
  loading?: boolean;
  /** Error message to display on the screen */
  message?: string;
};
const renderFile: React.FunctionComponent<FileItemShape> = ({
  name,
  url,
  onDelete,
  loading,
  message,
}) => (
  <File
    name={name}
    url={url}
    onDelete={onDelete}
    loading={loading}
    message={message}
  />
);

const renderFiles = flow(map(renderFile), wrapEachComponentWithKey);

const filesFromEvent = ({ target }: any) => [...target.files];

export const limitItems = (list: any, quota: any, used: any) => {
  const remainingQuota = quota - used;
  const end = remainingQuota >= 0 ? remainingQuota : 0;
  return list.slice(0, end);
};

type FileUploaderProps = {
  /** The text displayed on the button */
  buttonText: string;
  /** An action handler which is given the list of selected files from the html component */
  onChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    files: FileItemShape[]
  ) => void;
  /** List of valid file types for the html accept property  */
  validTypes?: string[];
  /** Limit how many files can be uploaded.  */
  maximumAmountOfFiles?: number;
  /** A queue of files to show in the ui */
  files: FileItemShape[];
} & Omit<
  React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >,
  'onChange'
>;

const FileUploader: React.FunctionComponent<FileUploaderProps> = ({
  buttonText,
  onChange,
  files,
  validTypes = [],
  maximumAmountOfFiles = 1,
  ...props
}) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const types =
    validTypes.length > 0 ? typesToAttribute(validTypes) : undefined;
  const allowMultipleFiles = maximumAmountOfFiles - files.length > 1;
  const maximumAmountOfFilesReached = files.length >= maximumAmountOfFiles;

  const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = filesFromEvent(event);
    const allowedFiles = limitItems(
      selectedFiles,
      maximumAmountOfFiles,
      files.length
    );
    onChange(event, allowedFiles);
    event.target.value = '';
  };

  return (
    <Div>
      <input
        className={styles.fileUploader_input__hidden}
        type="file"
        accept={types}
        ref={fileInput}
        onChange={changeHandler}
        multiple={allowMultipleFiles}
        {...props}
      />

      <Button
        variant="secondary"
        disabled={maximumAmountOfFilesReached}
        onClick={() => fileInput.current && fileInput.current.click()}
      >
        {buttonText}
      </Button>

      <Gutter />

      {renderFiles(files)}
    </Div>
  );
};

export default FileUploader;
